import { Questionnaires } from '@common/components/questionnaires';
import { QuestionnaireListDto } from '@common/dto/questionnaire/list-dto';
import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { ReceiverChooser } from '../components/receiver-chooser';
import { PatientInviteReceiver } from '../patient-invite.types';
import { IPatientInviteFlow, PatientInviteStep, PatientInviteStepConfig } from './types';
import { NewPatientFlowBasicProps, useNewPatientFlow } from './new-patient-flow';
import { useMessage } from '@common/hooks/message';
import { IndividualFlowBasicProps, useIndividualFlow } from './individual-flow';
import { MembersOfOneFamilyFlowBasicProps, useMembersOfOneFamilyFlow } from './member-of-one-family-flow';
import { PatientsSpecificCriteriaBasicProps, usePatientsSpecificCriteria } from './patients-specific-criteria';
import { InviteSendParams } from '@common/actions/invites';

export type PatientInviteProps = MembersOfOneFamilyFlowBasicProps & IndividualFlowBasicProps & NewPatientFlowBasicProps & PatientsSpecificCriteriaBasicProps & {
  onClose: () => void;
  onSubmit: (params: InviteSendParams) => Promise<void>;
  getQuestionnaires: () => Promise<Array<QuestionnaireListDto>>;
};

export const usePatientInviteController = (props: PatientInviteProps) => {
  const { 
    getPedigrees,
    getQuestionnaires, 
    getBasicFolders, 
    onSubmit, 
    getPedigreeFolders, 
    getIndividuals, 
    getFoldersForAddingPedigreeColumn, 
    getFolderFieldsForAddingPedigreeColumn,
    getPedigreesColumns,
    getAdminSmartListFunctions,
    savePedigreesColumns,
    getFormats,
    createFormatFolder, 
    deleteFormat, 
    deleteFormatFolder, 
    getFolders,
    getFields,
    createPedigree,
    saveAdditionalDatasheetFormatData,
    getAdditionalDatasheetFormatData,
    getIndividualsColumns,
    updateIndividual,
    getIndividualFamilyData,
    getFhqInviteRelatives,
    getPedigreeMembersCount,
    createPedigreeAndMoveToFolder,
    getIndividualHistoryLogs,
    getInviteMetadata,
    checkPedigreeNameExists,
    getQueryFormat,
    saveQueryFormats,
    getFormatFolders,
    getFhqInviteIndividuals,
    showConfirmMessage
  } = props;
  const [receiver, setReceiver] = React.useState<PatientInviteReceiver | undefined>();
  const [currentStep, setCurrentStep] = React.useState(PatientInviteStep.questionnaires);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = React.useState<QuestionnaireListDto | undefined>();
  const { showErrorMessage, messageProps, closeMessage, showInfoMessage } = useMessage();
  
  const newPatientFlow = useNewPatientFlow({
    saveAdditionalDatasheetFormatData,
    getAdditionalDatasheetFormatData,
    createPedigree,
    getBasicFolders,
    onSubmit,
    setCurrentStep,
    showErrorMessage,
    questionnaire: selectedQuestionnaire,
  });

  const individualFlow = useIndividualFlow({
    showInfoMessage,
    checkPedigreeNameExists,
    createPedigreeAndMoveToFolder,
    getIndividualFamilyData,
    updateIndividual,
    getIndividualsColumns,
    getIndividuals,
    getPedigreeFolders,
    onSubmit,
    setCurrentStep,
    showErrorMessage,
    questionnaire: selectedQuestionnaire,
  });

  const memberOfOneFamilyFlow = useMembersOfOneFamilyFlow({
    saveAdditionalDatasheetFormatData,
    getPedigreesColumns,
    savePedigreesColumns,
    getAdminSmartListFunctions,
    getFolderFieldsForAddingPedigreeColumn,
    getFoldersForAddingPedigreeColumn,
    getIndividualHistoryLogs,
    getPedigrees,
    onSubmit,
    setCurrentStep,
    showErrorMessage,
    showInfoMessage,
    getFhqInviteRelatives,
    getPedigreeMembersCount,
    questionnaire: selectedQuestionnaire,
    getInviteMetadata
  });

  const patientsSpecificCriteria = usePatientsSpecificCriteria({
    showConfirmMessage,
    getInviteMetadata,
    saveAdditionalDatasheetFormatData,
    updateIndividual,
    getFields,
    getFormats,
    showInfoMessage,
    createFormatFolder, 
    deleteFormat, 
    deleteFormatFolder, 
    getFolders,
    getIndividualHistoryLogs,
    getFhqInviteIndividuals,
    onSubmit,
    setCurrentStep,
    showErrorMessage,
    questionnaire: selectedQuestionnaire,
    getQueryFormat: getQueryFormat,
    saveQueryFormats,
    getFormatFolders
  });

  const flowConfig: Record<PatientInviteReceiver, IPatientInviteFlow> = {
    [PatientInviteReceiver.newPatient]: newPatientFlow,
    [PatientInviteReceiver.individual]: individualFlow,
    [PatientInviteReceiver.membersOfOneFamily]: memberOfOneFamilyFlow,
    [PatientInviteReceiver.patientsWhoMeetCriteria]: patientsSpecificCriteria,
  };
  const currentFlow = receiver && currentStep !== PatientInviteStep.receiverChooser 
    ? flowConfig[receiver] 
    : undefined;

  const commonStepsConfig: Partial<Record<PatientInviteStep, PatientInviteStepConfig>> = {
    [PatientInviteStep.questionnaires]: {
      submitBtnProps: {
        disabled: !selectedQuestionnaire,
      },
      component: (
        <Questionnaires
          classes={{
            body: 'PatientInvite__questionnaires'
          }}
          getData={getQuestionnaires} 
          title={I18n.t("Select a Questionnaire you want to invite patients to")}
          selectedQuestionnaire={selectedQuestionnaire} 
          onSelectedQuestionnaireChange={setSelectedQuestionnaire}
        />
      ),
      onSubmit: () => {
        if (Boolean(selectedQuestionnaire?.datasheetFormatName?.length) || selectedQuestionnaire?.fhq4) {
          setCurrentStep(PatientInviteStep.receiverChooser);

          return;
        }

        showInfoMessage(I18n.t("Selected questionnaire doesn't have Datasheet Format"));
      }
    },
    [PatientInviteStep.receiverChooser]: {
      submitBtnProps: {
        disabled: !receiver,
      },
      component: (
        <ReceiverChooser
          onChange={setReceiver}
          value={receiver}
        />
      ),
      previousStep: PatientInviteStep.questionnaires,
      onGoToPreviousStep: () => {
        setReceiver(undefined);
      },
      onSubmit: () => {
        // TODO rethink this
        const flow = receiver ? flowConfig[receiver] : undefined;

        if (!flow) {
          return;
        }

        const nextStep = flow.getFirstStep();

        setCurrentStep(nextStep);
      }
    },
  };

  const currentStepConfig = currentFlow ? currentFlow.stepsConfig[currentStep] : commonStepsConfig[currentStep];

  const goToPreviousStep = React.useCallback(() => {
    if (currentStepConfig?.previousStep) {
      setCurrentStep(currentStepConfig.previousStep);

      if (currentStepConfig.onGoToPreviousStep) {
        currentStepConfig.onGoToPreviousStep()
      }
    }
  }, [currentStepConfig?.previousStep, currentStepConfig?.onGoToPreviousStep]);

  return {
    onGoToPreviousStep: currentStepConfig?.previousStep ? goToPreviousStep : undefined,
    handleSubmit: currentStepConfig?.onSubmit,
    submitting: currentStepConfig?.submitting,
    submitBtnProps: currentStepConfig?.submitBtnProps,
    component: currentStepConfig?.component,
    messageProps,
    closeMessage,
  };
};